import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Download } from "lucide-react";
import { COMPANY } from "../data/site";

// 3 rotating slides — different products, different visuals
const SLIDES = [
  {
    id: "drt",
    img: "https://customer-assets.emergentagent.com/job_weld-overlay-tech/artifacts/3o6qbkbj_DRT%20Render%20Model.26.webp",
    eyebrow: "Welding Automation · OEM",
    tag: "Dynamic Rotational Torch · DRT"
  },
  {
    id: "boiler",
    img: "https://customer-assets.emergentagent.com/job_weld-overlay-tech/artifacts/vmw6yy5s_Boiler%20Tube%20WOL%20System%20.png",
    eyebrow: "Boiler Tube Cladding · ±0.2 mm",
    tag: "Containerized BTC Station"
  },
  {
    id: "tubewall",
    img: "https://customer-assets.emergentagent.com/job_weld-overlay-tech/artifacts/b92pxada_Boiler%20Wall%20System%20render%20Image.png",
    eyebrow: "Twin-Torch Overlay · Tube Wall",
    tag: "Twin Head Tube Wall System"
  },
  {
    id: "omni-laser",
    img: "https://customer-assets.emergentagent.com/job_weld-overlay-tech/artifacts/3qxwhh6q_OMNI%20LASER%20CLAD%20%202.0%20.png",
    eyebrow: "Laser Clad · OMNI 2.0",
    tag: "OMNI Laser Clad System"
  }
];

export const Hero = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[92svh] w-full overflow-hidden bg-[#0F172A] pt-36 md:pt-44"
    >
      {/* Crossfading product photographs */}
      <div className="absolute inset-0">
        <AnimatePresence initial={false} mode="sync">
          <motion.img
            key={SLIDES[idx].id}
            src={SLIDES[idx].img}
            alt="Weldmatix industrial system"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 0.55, scale: 1 }}
            exit={{ opacity: 0, scale: 1 }}
            transition={{ duration: 1.0, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`hero-slide-${SLIDES[idx].id}`}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/70 to-[#0F172A]/30" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="absolute inset-0 tech-grid-light opacity-40" />
      </div>

      {/* Slide pagination dots — bottom right of hero */}
      <div className="absolute bottom-32 md:bottom-24 right-6 lg:right-12 z-10 flex items-center gap-2" data-testid="hero-pagination">
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setIdx(i)}
            data-testid={`hero-dot-${i}`}
            aria-label={`Slide ${i + 1}`}
            className={`transition-all duration-300 ${
              i === idx
                ? "w-10 h-[3px] bg-[#0EA5E9]"
                : "w-4 h-[3px] bg-white/30 hover:bg-white/60"
            }`}
          />
        ))}
      </div>

      {/* Vertical mono labels */}
      <div
        className="hidden md:block absolute left-6 top-1/2 -translate-y-1/2 font-mono-wm text-[10px] tracking-[0.4em] uppercase text-white/40 rotate-180"
        style={{ writingMode: "vertical-rl" }}
      >
        WELDING AUTOMATION · GLOBAL OEM
      </div>
      <div
        className="hidden md:block absolute right-6 top-1/2 -translate-y-1/2 font-mono-wm text-[10px] tracking-[0.4em] uppercase text-white/40"
        style={{ writingMode: "vertical-rl" }}
      >
        WELDMATIX · FUSION
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-4 mb-7"
        >
          <span className="w-10 h-px bg-[#0EA5E9]" />
          <AnimatePresence mode="wait">
            <motion.span
              key={SLIDES[idx].id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35 }}
              className="font-mono-wm text-[11px] tracking-[0.3em] uppercase text-[#0EA5E9]"
              data-testid="hero-eyebrow"
            >
              {SLIDES[idx].eyebrow}
            </motion.span>
          </AnimatePresence>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display uppercase tracking-tight text-white leading-[0.98] text-[40px] sm:text-5xl md:text-6xl lg:text-[78px] max-w-5xl"
        >
          Advanced Welding<br />
          Automation & <span className="text-[#0EA5E9]">Weld Overlay</span><br />
          Solutions
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-7 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed"
        >
          Engineering high-performance cladding systems, weld overlay equipment, and
          industrial automation for critical industries worldwide.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-9 flex flex-col sm:flex-row gap-3"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-consultation"
            className="group inline-flex items-center justify-between gap-6 bg-[#0EA5E9] hover:bg-[#0284C7] text-white px-7 py-4 text-sm uppercase tracking-[0.2em] font-medium transition-colors min-w-[260px]"
          >
            Request Consultation
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
          <a
            href={COMPANY.brochure_url}
            target="_blank"
            rel="noopener noreferrer"
            download
            data-testid="hero-cta-catalogue"
            className="group inline-flex items-center justify-between gap-6 border border-white/30 hover:border-white text-white px-7 py-4 text-sm uppercase tracking-[0.2em] transition-colors min-w-[260px] bg-white/0 hover:bg-white/5"
          >
            Download Catalogue
            <Download className="w-5 h-5" />
          </a>
        </motion.div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-white/15"
        >
          {[
            { v: "15+", l: "Years Engineering" },
            { v: "ISO 9001", l: "Certified OEM" },
            { v: "9", l: "Industries Served" },
            { v: "24/7", l: "Remote Diagnostics" }
          ].map((s, i) => (
            <div key={i} className="border-r last:border-r-0 border-white/15 py-6 px-2">
              <div className="font-display text-2xl md:text-4xl text-white">{s.v}</div>
              <div className="font-mono-wm text-[10px] tracking-[0.25em] uppercase text-white/55 mt-2">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
